import isNil from 'lodash/isNil';
import omitBy from 'lodash/omitBy';
import useSWR, { SWRConfiguration } from 'swr';

import { IRaffleWinner } from 'containers/Campaign/Raffle/DrawSection/interfaces';
import { IAnyCampaign } from 'containers/Campaign/types';
import { IRaffleTicketTransaction } from 'containers/Customer/types';
import { IDetailResponse, IPaginatedResponse, IResponseMeta } from 'utils/types';

import { fetcher } from './utils';

export type IStampCard = Record<string, any>;
export type IStampTransaction = Record<string, any>;
export type IStampTransactionRequest = Record<string, any>;
export type IInstantOutcomeTransaction = Record<string, any>;
export type IProgressPointsTransaction = Record<string, any>;
export interface IProgressPointsMeta extends IResponseMeta {
  total_points?: number;
}
export type IProgressPointsResponse = IPaginatedResponse<IProgressPointsTransaction> & {
  meta: IProgressPointsMeta;
};

export const useCampaign = (id?: number | string | null, params?: Record<string, any>) => {
  const key = id
    ? params
      ? { url: `/v4/dash/campaigns/${id}`, params }
      : `/v4/dash/campaigns/${id}`
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<IAnyCampaign>>(
    key,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCampaignList = (params: object, shouldFetch: boolean = true) => {
  const sanitizedParams = omitBy(params, isNil);
  const key = shouldFetch ? { url: '/v4/dash/campaigns', params: sanitizedParams } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IAnyCampaign>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignsWithIds = (ids: number[] = []) => {
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IAnyCampaign>>(
    ids.length > 0 ? `/v4/dash/campaigns?id=${ids.join(',')}&limit=1000` : null,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignsAutoFillStampsOnIds = (ids: number[] = []) => {
  const sortedIds = [...ids].sort((a, b) => a - b);
  const { data, isLoading, isValidating } = useSWR<IDetailResponse<IAnyCampaign>[]>(
    ids.length > 0 ? ['campaigns-auto-fill-stamps', ...sortedIds] : null,
    () => Promise.all(ids.map(id => fetcher(`/v4/dash/campaigns/${id}`)))
  );

  const autoFillOnIds = new Set<number>(
    (data ?? []).filter(res => res?.data?.auto_fill_stamps === true).map(res => res.data.id)
  );

  return { autoFillOnIds, isLoading: isLoading || isValidating };
};

export const useCampaignStampCardList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId ? { url: `/v4/dash/campaigns/${campaignId}/stamp_cards`, params } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IStampCard>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignStampCard = (
  campaignId?: number | string | null,
  cardId?: number | string | null
) => {
  const key =
    campaignId && cardId ? `/v4/dash/campaigns/${campaignId}/stamp_cards/${cardId}` : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IDetailResponse<IStampCard>>(
    key,
    fetcher
  );

  return { data: data?.data, isLoading, error, mutate, isValidating };
};

export const useCampaignStampCardTransactionList = (
  campaignId?: number | string | null,
  cardId?: number | string | null,
  params?: Record<string, any>,
  config?: SWRConfiguration
) => {
  const key =
    campaignId && cardId
      ? { url: `/v4/dash/campaigns/${campaignId}/stamp_cards/${cardId}/stamp_transactions`, params }
      : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IStampTransaction>
  >(key, fetcher, config);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignStampTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>,
  config?: SWRConfiguration
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/stamp_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IStampTransaction>
  >(key, fetcher, config);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignStampTransactionRequestList = (
  campaignId?: number | string | null,
  params?: Record<string, any>,
  config?: SWRConfiguration
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/stamp_transaction_requests`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IStampTransactionRequest>
  >(key, fetcher, config);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignInstantOutcomeTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>,
  config?: SWRConfiguration
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/instant_outcome_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IInstantOutcomeTransaction>
  >(key, fetcher, config);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignProgressPointTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>,
  config?: SWRConfiguration
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/progress_points_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IProgressPointsResponse>(
    key,
    fetcher,
    config
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export type IQuestTask = Record<string, any>;

export const useQuestTaskList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId ? { url: `/v4/dash/campaigns/${campaignId}/quest_tasks`, params } : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<IQuestTask>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useRaffleWinnersList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/raffle_winners`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IRaffleWinner>
  >(key, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignGameTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/game_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<any>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignQuestTaskTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/quest_task_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<IPaginatedResponse<any>>(
    key,
    fetcher
  );

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};

export const useCampaignRaffleTicketTransactionList = (
  campaignId?: number | string | null,
  params?: Record<string, any>
) => {
  const key = campaignId
    ? { url: `/v4/dash/campaigns/${campaignId}/raffle_ticket_transactions`, params }
    : null;
  const { data, isLoading, error, mutate, isValidating } = useSWR<
    IPaginatedResponse<IRaffleTicketTransaction>
  >(key, fetcher);

  return { data: data?.data, meta: data?.meta, isLoading, error, mutate, isValidating };
};
